import React, { useMemo } from 'react';
import { Form } from 'react-final-form';

import Select from '@bidding/form/components/fields/Select';

// Calculate-the-price hero widget. Reuses the bidding wizard's
// `<Select>` so dropdown look matches Order Details inside the cabinet.
// `react-final-form` shell wires the Field bindings inside `<Select>`.
//
// Own `.calc-widget` BEM namespace — does NOT reuse the brand-front's
// `.calculate__*` classes because those cascade button + background
// rules from `redesign.css` that override `.bidding-select` styling and
// repaint the dropdowns green/blue. Brand-owned hero layout (gradient
// page background, position) stays brand-side; widget owns its own
// inner card layout.
//
// Submit posts to `/order` as a GET — the bidding wizard's `parseUrl()`
// reads `service_type_id`, `work_type_id`, `deadline_id`,
// `number_pages` from the query string and pre-fills step 1.

const pagesOptions = (pages) =>
    Object.entries(pages || {}).map(([key, label]) => {
        // Backend ships "275 words / 1 pages" — re-format to
        // "1 page (275 words)" so the dropdown reads naturally.
        const pretty = String(label).replace('words', 'words)').replace('/ ', '(');
        const text = String(key) === '1' ? pretty.replace('pages', 'page') : pretty;
        return { id: String(key), name: text };
    });

const normalize = (rows = []) =>
    rows.map((r) => ({ id: String(r.id), name: String(r.name) }));

export default function CalcWidget({ config, variant = 'hero' }) {
    const serviceTypes = useMemo(() => normalize(config.serviceTypes || []), [config.serviceTypes]);
    const workTypes = useMemo(
        () => normalize(config.workTypes?.data || config.workTypes || []),
        [config.workTypes],
    );
    const deadlines = useMemo(() => normalize(config.deadlines || []), [config.deadlines]);
    const pages = useMemo(() => pagesOptions(config.pages), [config.pages]);

    const initialValues = useMemo(() => ({
        service_type_id: serviceTypes[0]?.id || '',
        work_type_id:    workTypes[0]?.id || '',
        deadline_id:     String(config.defaultDeadlineId ?? deadlines[0]?.id ?? ''),
        number_pages:    pages[0]?.id || '1',
    }), [serviceTypes, workTypes, deadlines, pages, config.defaultDeadlineId]);

    const onSubmit = (values) => {
        const qs = new URLSearchParams(values).toString();
        window.location.assign(`/order?${qs}`);
    };

    return (
        // Carry the brand-front legacy hook class `calc-form` so existing
        // per-brand CSS that paints `.calc-form button { background: ... }`
        // still styles the Continue button (e.g. bidforwriting paints it
        // coral red, tutoriage green). The `.calc-widget` override on
        // `.bidding-select__control` is more specific so it still wins
        // for the dropdown triggers.
        <div className={`calc-widget calc-form calc-widget--${variant}`}>
            <Form onSubmit={(values) => onSubmit(values)} initialValues={initialValues}>
                {({ handleSubmit, values }) => {
                    const perPageMin = config.biddingPrice?.perPageMin;
                    const showPrice = Number.isFinite(perPageMin);
                    const startingPrice = showPrice
                        ? Math.round(perPageMin * Number(values.number_pages || 1))
                        : null;

                    return (
                        <form className="calc-widget__form" onSubmit={handleSubmit}>
                            <div className="calc-widget__head">
                                <h3 className="calc-widget__title">Calculate the price</h3>
                                {showPrice && (
                                    <div className="calc-widget__price">
                                        <span className="calc-widget__price-label">Minimum Price</span>
                                        <span className="calc-widget__price-amount">${startingPrice}</span>
                                    </div>
                                )}
                            </div>
                            <div className="calc-widget__row">
                                <div className="calc-widget__field">
                                    <Select name="service_type_id" options={serviceTypes} isSearchable={false} />
                                </div>
                                <div className="calc-widget__field">
                                    <Select name="work_type_id" options={workTypes} isSearchable={false} />
                                </div>
                                <div className="calc-widget__field">
                                    <Select name="deadline_id" options={deadlines} isSearchable={false} />
                                </div>
                                <div className="calc-widget__field">
                                    <Select name="number_pages" options={pages} isSearchable={false} />
                                </div>
                                <button type="submit" className="calc-widget__submit">Continue</button>
                            </div>
                        </form>
                    );
                }}
            </Form>
        </div>
    );
}
